import { CheckIcon } from "lucide-react";
import type { MouseEvent, PointerEvent } from "react";

import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

interface SidebarThreadSettleControlProps {
  readonly threadId: string;
  readonly title: string;
  readonly onPointerDown: (event: PointerEvent<HTMLButtonElement>) => void;
  readonly onSettle: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly placement?: "slot" | "cluster";
}

/**
 * t3code's row-hover Settle button: icon and label, with no confirmation step.
 * Settling is a lightweight, reversible toggle.
 *
 * Same sizing convention as `SidebarThreadArchiveControl`: `--acard` where the
 * Activity card declares it, `1rem` elsewhere. The default slot placement owns
 * its reveal wrapper. The cluster placement is already inside a shared action
 * reveal. Settled rows render Restore instead of Settle.
 */
/**
 * `has-[:focus-visible]`, scoped to this wrapper — not the row's
 * `group-focus-within`.
 *
 * The card it sits in is `tabIndex={0}`, so any click on the card focuses it
 * and focus-within fired for the whole row: the control pinned itself over the
 * status label and stayed there once the pointer moved away. Upstream keys the
 * same reveal on `has-[:focus-visible]` for exactly this reason, and its
 * comment spells out the case that made it switch — a mouse click leaves the
 * Settle button focused, and focus-within would then never cross-fade back.
 */
const SETTLE_BUTTON_WRAPPER_CLASS =
  "pointer-events-none absolute inset-y-0 right-0 inline-flex shrink-0 items-center opacity-0 transition-opacity duration-150 group-hover/menu-sub-item:pointer-events-auto group-hover/menu-sub-item:static group-hover/menu-sub-item:opacity-100 has-[:focus-visible]:pointer-events-auto has-[:focus-visible]:static has-[:focus-visible]:opacity-100";
const SETTLE_BUTTON_CLASS =
  "-mr-1 inline-flex cursor-pointer items-center gap-[calc(var(--acard,1rem)*0.25)] rounded-md px-[calc(var(--acard,1rem)*0.375)] text-[length:calc(var(--acard,1rem)*0.75)] leading-[var(--acard,1rem)] font-medium text-muted-foreground transition-colors hover:bg-accent/65 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring";
const SETTLE_ICON_CLASS = "size-[calc(var(--acard,1rem)*0.875)]";

export function SidebarThreadSettleControl(props: SidebarThreadSettleControlProps) {
  const settleButton = (
    <button
      type="button"
      data-thread-selection-safe
      data-testid={`thread-settle-${props.threadId}`}
      aria-label={`Settle ${props.title}`}
      className={SETTLE_BUTTON_CLASS}
      onPointerDown={props.onPointerDown}
      onClick={props.onSettle}
    >
      <CheckIcon className={SETTLE_ICON_CLASS} />
      Settle
    </button>
  );

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          props.placement === "cluster" ? (
            settleButton
          ) : (
            <div className={SETTLE_BUTTON_WRAPPER_CLASS}>{settleButton}</div>
          )
        }
      />
      <TooltipPopup side="top">Settle thread</TooltipPopup>
    </Tooltip>
  );
}
